import { useState } from 'react';
import { useFeedback } from '../common/Feedback';
import { LogIn, Loader2 } from 'lucide-react';
import { useCatalog } from '../../hooks/useCatalog';
import { CHROME_WEBSTORE_URL } from '../../utils/constants';
import { abrirEMostrarErro, useBotaoAbrir } from '../../utils/extensaoLictor';
import {
  AVISO_INSTALAR_EXTENSAO,
  AcaoDoAcessar,
  decidirAcaoDoAcessar,
  mostrarAvisoDaExtensao,
  temBotaoAcessar,
  urlParaAcessar,
} from '../../utils/acessar';

interface CredencialDoAcessar {
  id: string;
  catalog_key?: string | null;
  url_patterns?: string[] | null;
}

/**
 * D23: estado do "Acessar" de uma credencial. `ativo` = a credencial tem endereço (o botão aparece);
 * `acao` = o que o clique faz agora; `mostrarAviso` = a extensão não respondeu (mostra o aviso).
 */
export function useAcessar(credencial: CredencialDoAcessar) {
  const { catalog } = useCatalog();
  const ativo = temBotaoAcessar(credencial, catalog);
  const botao = useBotaoAbrir(ativo);
  return {
    ativo,
    acao: decidirAcaoDoAcessar(botao, urlParaAcessar(credencial, catalog)),
    mostrarAviso: ativo && mostrarAvisoDaExtensao(botao),
  };
}

interface BotaoAcessarProps {
  credentialId: string;
  acao: AcaoDoAcessar;
  /** Linha da visão Lista: botão pequeno. Sem isso, o botão do cartão (largura total, como o "Assinar PDF"). */
  compacto?: boolean;
}

/** Depois que a extensão responde, o botão continua "ocupado" mais um instante (evita cliques repetidos enquanto a aba abre). */
const MANTER_OCUPADO_DEPOIS_MS = 1500;

/** D23: "Acessar", o botão principal da credencial (cor primária, nunca oculto). */
export function BotaoAcessar({ credentialId, acao, compacto = false }: BotaoAcessarProps) {
  const { avisar } = useFeedback();
  // Feedback visual: enquanto a extensão faz o login (pode levar alguns segundos), mostra o spinner e ignora novos cliques.
  const [entrando, setEntrando] = useState(false);
  const estilo: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: compacto ? 'var(--space-1)' : 'var(--space-2)',
    width: compacto ? undefined : '100%',
    boxSizing: 'border-box',
    padding: compacto ? '4px 10px' : 'var(--space-2)',
    fontSize: compacto ? '11px' : undefined,
    background: 'var(--color-primary-600)',
    color: 'var(--color-primary-50)',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    cursor: acao.tipo === 'nada' ? 'not-allowed' : entrando ? 'progress' : 'pointer',
    opacity: acao.tipo === 'nada' ? 0.6 : entrando ? 0.85 : 1,
    fontWeight: 'var(--font-medium)',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  };
  const conteudo = entrando ? (
    <>
      <Loader2 size={compacto ? 14 : 16} style={{ animation: 'spin 1s linear infinite' }} aria-hidden="true" />
      Entrando...
    </>
  ) : (
    <>
      <LogIn size={compacto ? 14 : 16} />
      Acessar
    </>
  );

  // Sem a extensão: um link comum para a página de login, numa aba nova (o navegador não bloqueia).
  if (acao.tipo === 'aba') {
    return (
      <a href={acao.url} target="_blank" rel="noopener noreferrer" className="btn" style={estilo}>
        {conteudo}
      </a>
    );
  }

  return (
    <button
      type="button"
      disabled={acao.tipo === 'nada'}
      aria-busy={entrando}
      onClick={(e) => {
        if (acao.tipo !== 'extensao' || entrando) return;
        // Só um clique de verdade do usuário (nunca um .click() de script) pede à extensão.
        if (!e.nativeEvent.isTrusted) return;
        setEntrando(true);
        // D16b: se a extensão disser que falhou, o erro aparece num aviso (como os outros erros do cartão).
        void abrirEMostrarErro(credentialId, window, (texto) => avisar(texto, 'erro')).finally(() => {
          setTimeout(() => setEntrando(false), MANTER_OCUPADO_DEPOIS_MS);
        });
      }}
      title={acao.tipo === 'extensao' ? 'Abrir já logado' : undefined}
      style={estilo}
    >
      {conteudo}
    </button>
  );
}

/** D23: aviso (texto aprovado) quando a extensão não responde, com o link para instalá-la. */
export function AvisoInstalarExtensao({ centralizado = false }: { centralizado?: boolean }) {
  return (
    <a
      href={CHROME_WEBSTORE_URL}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'block',
        fontSize: 'var(--text-xs)',
        color: 'var(--color-primary-400)',
        textDecoration: 'underline',
        textUnderlineOffset: '2px',
        textAlign: centralizado ? 'center' : 'left',
        overflowWrap: 'anywhere',
      }}
    >
      {AVISO_INSTALAR_EXTENSAO}
    </a>
  );
}
